import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Gem,
  Scissors,
  Waves,
  MessageCircle,
  MapPin,
  Ruler,
  PackageCheck,
  Crown,
  Shirt,
  Sparkles,
  Baby,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, formatApiError } from "../api";
import { useSettings } from "../context/SettingsContext";
import { useCart } from "../context/CartContext";
import ProductCard from "../components/ProductCard";
import Couture3DCarousel, { COUTURE_3D_ITEMS } from "../components/Couture3DCarousel";
import { normalizeHeroSlides } from "../data/heroSlides";
import {
  createDiscoveryRunId,
  getDiscoveryRecommendations,
  recordCategoryInterest,
} from "../utils/recommendations";
import {
  normalizeProductCategoryAssignments,
  productMatchesCategory,
} from "../utils/productCategoryAssignments";

const FALLBACK_HERO =
  "/assets/couture/women_bridal.jpg";
const FALLBACK_BANNER =
  "/assets/editorial_banner.jpg";
const FALLBACK_STORY =
  "/assets/story_craft.jpg";

const CATEGORY_PRESENTATION = {
  collection: {
    image: "/assets/couture/heritage_drape.jpg",
    eyebrow: "The signature edit",
    description: "Modern finesse, thoughtful drapes and statement pieces for a wardrobe with presence.",
  },
  "mens-wear": {
    image: "/assets/couture/men_sherwani.jpg",
    eyebrow: "Refined tailoring",
    description: "Ceremonial layers and contemporary separates shaped with quiet confidence.",
  },
  "womens-wear": {
    image: "/assets/couture/women_bridal.jpg",
    eyebrow: "Fluid occasionwear",
    description: "Expressive silhouettes that move between heritage detail and modern ease.",
  },
  "kids-wear": {
    image: "/assets/couture/kids_festive.jpg",
    eyebrow: "Little celebrations",
    description: "Light, joyful festive pieces designed for comfort, movement and memory.",
  },
};

const HOUSE_CODES = [
  {
    icon: Scissors,
    index: "01",
    title: "Craft, reinterpreted",
    body: "Indian embroideries, textures and artisanal techniques are considered through a distinctly contemporary lens.",
  },
  {
    icon: Waves,
    index: "02",
    title: "Movement, always",
    body: "Fluid forms and balanced construction let every silhouette feel expressive, comfortable and effortless to wear.",
  },
  {
    icon: Gem,
    index: "03",
    title: "Distinctly yours",
    body: "Colour, detail and proportion come together in pieces that celebrate individuality rather than prescribe it.",
  },
];

const ATELIER_ASSURANCE = [
  {
    icon: MapPin,
    title: "Hyderabad atelier",
    body: "Contemporary Indian occasionwear, considered close to the craft.",
    to: "/about",
  },
  {
    icon: Ruler,
    title: "Custom sizing",
    body: "Personalised sizing is available on request and subject to design feasibility.",
    to: "/customization",
  },
  {
    icon: PackageCheck,
    title: "Considered dispatch",
    body: "Ready pieces are prepared for dispatch within five to seven business days.",
    to: "/contact",
  },
];

const CATEGORY_ICONS = [Gem, Shirt, Sparkles, Baby];

function NewsletterForm({ block }) {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/api/newsletter", { email, website });
      if (data.email_sent) toast.success(data.message || "Welcome to the house. Please check your inbox.");
      else toast.warning(data.message || "Your subscription has been saved.");
      setEmail("");
      setWebsite("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid="newsletter-form">
      <label className="newsletter-honeypot" aria-hidden="true">
        Website
        <input
          type="text"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </label>
      <input
        type="email"
        placeholder="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        data-testid="newsletter-email-input"
      />
      <button type="submit" disabled={busy} aria-busy={busy} data-testid="newsletter-submit-button">
        {busy ? "Sending..." : block.button_label || "Subscribe"}
      </button>
    </form>
  );
}

export default function Home() {
  const { settings, homepage, whatsappLink } = useSettings();
  const { wishlist, cart } = useCart();
  const [featured, setFeatured] = useState([]);
  const [topRated, setTopRated] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [oldDeals, setOldDeals] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [activeShelf, setActiveShelf] = useState("bestsellers");
  const productRailRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [discoveryProducts, setDiscoveryProducts] = useState([]);
  const [discoveryMeta, setDiscoveryMeta] = useState({ personalized: false, preferredCategory: null });
  const discoveryRunIdRef = useRef(createDiscoveryRunId());
  const discoverySignalsRef = useRef({ wishlistIds: wishlist, cartItems: cart });

  useEffect(() => {
    (async () => {
      setCatalogLoading(true);
      setCatalogError(false);
      try {
        const [pRes, cRes] = await Promise.all([
          api.get("/api/products", { params: { limit: 500 } }),
          api.get("/api/categories", { params: { active: true } }),
        ]);
        const prods = pRes.data || [];
        setAllProducts(prods);
        setFeatured(prods.filter((p) => p.is_featured).slice(0, 8));
        setTopRated(prods.filter((p) => p.is_top_rated).slice(0, 8));
        setNewArrivals(prods.filter((p) => p.is_new_arrival).slice(0, 8));
        setOldDeals(prods.filter((p) => p.is_old_deal).slice(0, 8));
        setCategories((cRes.data || []).filter((category) => !category.parent_id));
        const counts = {};
        prods.forEach((product) => {
          normalizeProductCategoryAssignments(product).forEach((assignment) => {
            const slugKey = assignment.category_slug;
            const nameKey = assignment.category;
            if (slugKey) counts[slugKey] = (counts[slugKey] || 0) + 1;
            if (nameKey && nameKey !== slugKey) counts[nameKey] = (counts[nameKey] || 0) + 1;
          });
        });
        setCategoryCounts(counts);
      } catch (err) {
        console.error("Home data error:", err);
        setCatalogError(true);
      } finally {
        setCatalogLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!allProducts.length) {
      setDiscoveryProducts([]);
      return;
    }
    const mobile =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 560px)").matches;
    const result = getDiscoveryRecommendations(allProducts, {
      limit: 12,
      visibleLimit: mobile ? 9 : 12,
      wishlistIds: discoverySignalsRef.current.wishlistIds,
      cartItems: discoverySignalsRef.current.cartItems,
      runId: discoveryRunIdRef.current,
    });
    setDiscoveryProducts(result.items);
    setDiscoveryMeta(result.meta);
  }, [allProducts]);

  const hero = useMemo(() => homepage.hero || {}, [homepage.hero]);
  const shelfBlock = homepage.product_shelf || {};
  const assuranceBlock = homepage.assurance || {};
  const manifestoBlock = homepage.manifesto || {};
  const story = homepage.brand_story || {};
  const catBlock = homepage.categories || {};
  const topBlock = homepage.top_rated || { visible: true, eyebrow: "House favourites", heading: "Top rated." };
  const featBlock = homepage.featured || {};
  const houseCodesBlock = homepage.house_codes || {};
  const editorialBlock = homepage.editorial_banner || {};
  const newBlock = homepage.new_arrivals || {};
  const discoveryBlock = homepage.discovery || {};
  const stylingBlock = homepage.private_styling || {};
  const dealsBlock = homepage.old_deals || { visible: true, eyebrow: "Studio archive", heading: "Studio deals." };
  const launchBlock = homepage.launch_offer || {};
  const insta = homepage.instagram || {};
  const news = homepage.newsletter || {};

  const storyImg = story.image ? imgUrl(story.image) : FALLBACK_STORY;
  const bannerImg = editorialBlock.image ? imgUrl(editorialBlock.image) : settings.editorial_banner_image ? imgUrl(settings.editorial_banner_image) : FALLBACK_BANNER;
  const stylingImg = stylingBlock.image ? imgUrl(stylingBlock.image) : "/assets/couture/heritage_drape.jpg";
  const instaImages = insta.images && insta.images.length ? insta.images : [];

  const productsForShelf = (() => {
    if (activeShelf === "bestsellers") {
      const bestsellers = allProducts.filter((product) => product.is_bestseller);
      return bestsellers.length ? bestsellers : allProducts.filter((product) => product.is_featured);
    }
    return allProducts.filter((product) => productMatchesCategory(product, activeShelf));
  })();

  const selectShelf = (shelf) => {
    recordCategoryInterest(shelf, { strength: 1.5 });
    setActiveShelf(shelf);
    window.requestAnimationFrame(() => productRailRef.current?.scrollTo({ left: 0, behavior: "smooth" }));
  };

  const scrollProductRail = (direction) => {
    const rail = productRailRef.current;
    if (!rail) return;
    rail.scrollBy({ left: direction * Math.max(280, rail.clientWidth * 0.72), behavior: "smooth" });
  };

  const heroSlides = useMemo(() => {
    const configuredSlides = Array.isArray(hero.slides) ? hero.slides.filter(Boolean) : [];
    const hasConfiguredSlides = configuredSlides.length > 0;

    return normalizeHeroSlides(configuredSlides).map((slide, index) => {
      const fallback = COUTURE_3D_ITEMS[index] || COUTURE_3D_ITEMS[0];
      const normalizedSlide = {
        ...fallback,
        ...slide,
        id: slide.id || `cms-hero-${index}`,
        src: slide.image || slide.src || fallback.src,
        mobile_image: slide.mobile_image || null,
        fallbackSrc: fallback.src,
      };

      if (hasConfiguredSlides || index !== 0) return normalizedSlide;

      return {
        ...normalizedSlide,
        src: hero.image || settings.hero_image || normalizedSlide.src,
        tag: hero.eyebrow || normalizedSlide.tag,
        title: `${hero.title_line1 || "The Modern"} ${hero.title_line2 || "Trousseau"}`,
        subtitle: hero.subtitle || normalizedSlide.subtitle,
        description: hero.description || normalizedSlide.description,
        primary_cta_label: hero.primary_cta_label || normalizedSlide.primary_cta_label,
        primary_cta_link: hero.primary_cta_link || normalizedSlide.primary_cta_link,
        secondary_cta_label: hero.secondary_cta_label || normalizedSlide.secondary_cta_label,
        secondary_cta_link: hero.secondary_cta_link || normalizedSlide.secondary_cta_link,
      };
    });
  }, [hero, settings.hero_image]);

  return (
    <main className="home-page">
      {/* ============ IMMERSIVE COUTURE HERO ============ */}
      {hero.visible !== false && (
        <Couture3DCarousel items={heroSlides} />
      )}

      {shelfBlock.visible !== false && (
        <section className="home-hero-products" aria-label="Featured products">
          <div className="container">
            <div className="home-hero-products-head">
              <div>
                <span className="eyebrow">{shelfBlock.eyebrow}</span>
                <h2>{shelfBlock.heading}</h2>
              </div>
              <div className="home-product-rail-actions">
                <button type="button" onClick={() => scrollProductRail(-1)} aria-label="Previous products"><ChevronLeft size={18} /></button>
                <button type="button" onClick={() => scrollProductRail(1)} aria-label="Next products"><ChevronRight size={18} /></button>
              </div>
            </div>

            <div className="home-product-shelf-tabs" role="tablist" aria-label="Choose a product collection">
              {[{ id: "bestsellers", label: "Bestsellers", icon: Crown }, ...categories.map((category, index) => ({ id: category.slug, label: category.name, icon: CATEGORY_ICONS[index % CATEGORY_ICONS.length] }))].map(({ id, label, icon: Icon }) => (
                <button
                  type="button"
                  key={id}
                  role="tab"
                  aria-selected={activeShelf === id}
                  className={activeShelf === id ? "is-active" : ""}
                  onClick={() => selectShelf(id)}
                >
                  <span className="home-shelf-icon"><Icon size={20} strokeWidth={1.35} /></span>
                  <span>{label}</span>
                </button>
              ))}
            </div>

            <div className="home-hero-products-grid" ref={productRailRef} role="tabpanel">
              {catalogLoading ? (
                <div className="home-shelf-empty">Waking our atelier and loading the collection...</div>
              ) : catalogError ? (
                <div className="home-shelf-empty">The collection is taking longer than expected. Please refresh once.</div>
              ) : productsForShelf.length ? productsForShelf.map((product) => (
                  <ProductCard product={product} key={product.id} />
                )) : (
                  <div className="home-shelf-empty">{shelfBlock.empty_message}</div>
                )}
            </div>
            <Link to={shelfBlock.view_all_link || "/shop"} className="home-shelf-view-all text-link">{shelfBlock.view_all_label || "View the complete collection"} <ArrowUpRight size={14} /></Link>
          </div>
        </section>
      )}

      {/* ============ ATELIER ASSURANCE ============ */}
      {assuranceBlock.visible !== false && <section className="home-assurance" aria-label="Atelier assurance">
        <div className="home-assurance-inner">
          <div className="home-assurance-intro">
            <span className="eyebrow">{assuranceBlock.eyebrow}</span>
            <strong>{assuranceBlock.heading}</strong>
          </div>
          {ATELIER_ASSURANCE.map(({ icon: Icon, title, body, to }, index) => (
            <Link to={assuranceBlock[["atelier_link", "sizing_link", "dispatch_link"][index]] || to} className="home-assurance-item" key={title}>
              <Icon size={19} strokeWidth={1.35} />
              <span><strong>{assuranceBlock[["atelier_title", "sizing_title", "dispatch_title"][index]] || title}</strong><small>{assuranceBlock[["atelier_body", "sizing_body", "dispatch_body"][index]] || body}</small></span>
              <ArrowUpRight size={13} className="home-assurance-arrow" />
            </Link>
          ))}
          <a
            href={whatsappLink(assuranceBlock.whatsapp_message || "Hi Lalita Soujanya, I'd like help with an order.")}
            target="_blank"
            rel="noreferrer"
            className="home-assurance-item"
          >
            <MessageCircle size={19} strokeWidth={1.35} />
            <span><strong>{assuranceBlock.care_title || "Direct studio care"}</strong><small>{assuranceBlock.care_body || "Every order is personally confirmed with the studio."}</small></span>
            <ArrowUpRight size={13} className="home-assurance-arrow" />
          </a>
        </div>
      </section>}

      {/* ============ HOUSE MANIFESTO ============ */}
      {manifestoBlock.visible !== false && <section className="home-manifesto" data-testid="home-manifesto">
        <div className="home-manifesto-inner">
          <div className="home-manifesto-mark" aria-hidden="true">LS</div>
          <div className="home-manifesto-copy">
            <span className="eyebrow">{manifestoBlock.eyebrow}</span>
            <h2>{manifestoBlock.heading}</h2>
            <p>{manifestoBlock.body}</p>
            <Link to={manifestoBlock.cta_link || "/about"} className="text-link">{manifestoBlock.cta_label || "Discover the house"} <ArrowUpRight size={14} /></Link>
          </div>
          <p className="home-manifesto-note">
            {manifestoBlock.note}
          </p>
        </div>
      </section>}

      {/* ============ BRAND STORY ============ */}
      {story.visible !== false && (
        <section className="brand-story home-brand-story" data-testid="brand-story-section">
          <div className="container">
            <div className="grid">
              <div className="image">
                <img src={storyImg} alt="Artisan handwork in the Lalita Soujanya atelier" />
                <span className="home-story-caption">{story.image_caption || "Atelier notes / Hyderabad"}</span>
              </div>
              <div className="home-story-copy">
                <span className="eyebrow">{story.eyebrow}</span>
                <h2>
                  {story.heading}
                </h2>
                <p>{story.body}</p>
                <div className="home-story-principles" aria-label="Our design approach">
                  <span><b>01</b> {story.principle1 || "Indian craft"}</span>
                  <span><b>02</b> {story.principle2 || "Modern form"}</span>
                  <span><b>03</b> {story.principle3 || "Personal expression"}</span>
                </div>
                <Link to={story.cta_link || "/about"} className="text-link" data-testid="brand-story-cta">
                  {story.cta_label || "Our craft"} <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============ CATEGORIES ============ */}
      {catBlock.visible !== false && (
        <section id="categories" className="home-categories-section" data-testid="categories-section">
          <div className="container">
            <div className="section-head">
              <div>
                <span className="eyebrow">{catBlock.eyebrow}</span>
                <h2>{catBlock.heading}</h2>
              </div>
              <Link to={catBlock.view_all_link || "/shop"} className="text-link" data-testid="category-view-all-link">
                {catBlock.view_all_label || "View all looks"} <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="home-category-grid">
              {categories.map((c, i) => (
                <Link
                  key={c.id}
                  to={`/shop/${c.slug}`}
                  className="home-category-card"
                  data-testid={`category-link-${c.slug}`}
                >
                  <img src={c.image ? imgUrl(c.image) : CATEGORY_PRESENTATION[c.slug]?.image || FALLBACK_HERO} alt={c.name} loading="lazy" />
                  <span className="home-category-shade" />
                  <span className="home-category-index">{String(i + 1).padStart(2, "0")}</span>
                  <span className="home-category-copy">
                    <span className="eyebrow on-dark">{catBlock.card_eyebrow || "Explore"}</span>
                    <strong>{c.name}</strong>
                    <small>{c.description || CATEGORY_PRESENTATION[c.slug]?.description}</small>
                    <span className="home-category-link">
                      {categoryCounts[c.slug] || categoryCounts[c.name]
                        ? `${categoryCounts[c.slug] || categoryCounts[c.name]} pieces`
                        : "Discover collection"}
                      <ArrowUpRight size={13} />
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ TOP RATED (comes first, per spec) ============ */}
      {topBlock.visible !== false && topRated.length > 0 && (
        <section className="home-product-section home-product-section--favourites" data-testid="top-rated-section">
          <div className="container">
            <div className="section-head">
              <div>
                <span className="eyebrow">{topBlock.eyebrow}</span>
                <h2>{topBlock.heading}</h2>
                {topBlock.description && <p className="sub">{topBlock.description}</p>}
              </div>
              <Link to={topBlock.cta_link || "/shop"} className="text-link" data-testid="top-rated-view-all">
                {topBlock.cta_label || "Shop all"} <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="product-grid">
              {topRated.slice(0, 4).map((p) => (
                <ProductCard product={p} key={p.id} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ FEATURED ============ */}
      {featBlock.visible !== false && featured.length > 0 && (
        <section className="home-product-section home-product-section--featured" data-testid="featured-section">
          <div className="container">
            <div className="section-head">
              <div>
                <span className="eyebrow">{featBlock.eyebrow}</span>
                <h2>{featBlock.heading}</h2>
                {featBlock.description && <p className="sub">{featBlock.description}</p>}
              </div>
              <Link to={featBlock.cta_link || "/shop"} className="text-link" data-testid="featured-view-all-link">
                {featBlock.cta_label || "Shop all"} <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="product-grid">
              {featured.slice(0, 4).map((p) => (
                <ProductCard product={p} key={p.id} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ HOUSE CODES ============ */}
      {houseCodesBlock.visible !== false && <section className="house-codes" data-testid="house-codes-section">
        <span className="house-codes-monogram" aria-hidden="true">LS</span>
        <div className="container">
          <div className="house-codes-intro">
            <span className="eyebrow on-dark">{houseCodesBlock.eyebrow}</span>
            <h2>{houseCodesBlock.heading}</h2>
          </div>
          <div className="house-codes-grid">
            {HOUSE_CODES.map(({ icon: Icon, index, title, body }, itemIndex) => (
              <article className="house-code" key={index}>
                <div className="house-code-top"><span>{index}</span><Icon size={20} strokeWidth={1.4} /></div>
                <h3>{houseCodesBlock[`item${itemIndex + 1}_title`] || title}</h3>
                <p>{houseCodesBlock[`item${itemIndex + 1}_body`] || body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>}

      {/* ============ EDITORIAL BANNER ============ */}
      {editorialBlock.visible !== false && <section className="editorial-banner" data-testid="editorial-banner">
        <div className="image">
          <img src={bannerImg} alt="Editorial" />
        </div>
        <div className="content">
          <div className="editorial-banner-edition"><span>{editorialBlock.edition_left}</span><span>{editorialBlock.edition_right}</span></div>
          <span className="eyebrow on-dark">{editorialBlock.eyebrow || settings.editorial_banner_eyebrow}</span>
          <h2>
            {editorialBlock.title_line1 || settings.editorial_banner_title_line1}
            <br />
            <em>{editorialBlock.title_line2 || settings.editorial_banner_title_line2}</em>
          </h2>
          <p>{editorialBlock.body}</p>
          <Link to={editorialBlock.cta_link || "/shop/womens-wear"} className="btn btn-ghost" data-testid="editorial-shop-button">
            {editorialBlock.cta_label || "Shop occasion wear"} <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>}

      {/* ============ NEW ARRIVALS ============ */}
      {newBlock.visible !== false && newArrivals.length > 0 && (
        <section className="home-new-arrivals" data-testid="new-arrivals-section">
          <div className="container home-new-arrivals-layout">
            <div className="home-new-arrivals-intro">
              <span className="eyebrow">{newBlock.eyebrow}</span>
              <h2>{newBlock.heading}</h2>
              <p>{newBlock.description || "The newest expressions from the studio, made for the celebrations ahead."}</p>
              <Link to={newBlock.cta_link || "/shop"} className="text-link" data-testid="new-arrivals-view-all-link">
                {newBlock.cta_label || "View arrivals"} <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="product-grid home-new-arrivals-grid">
              {newArrivals.slice(0, 4).map((p) => (
                <ProductCard product={p} key={p.id} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ PERSONAL DISCOVERY ============ */}
      {discoveryBlock.visible !== false && discoveryProducts.length > 0 && (
        <section className="home-discovery" data-testid="home-discovery-section">
          <div className="container">
            <div className="home-discovery-head">
              <div>
                <span className="eyebrow">{discoveryBlock.eyebrow || "The private edit"}</span>
                <h2>{discoveryBlock.heading || "Discovered for you."}</h2>
              </div>
              <div className="home-discovery-note">
                <Sparkles size={17} strokeWidth={1.35} aria-hidden="true" />
                <p>
                  {discoveryMeta.personalized && discoveryMeta.preferredCategory
                    ? (discoveryBlock.personalized_message || "Inspired by your interest in {category}, with a few unexpected finds.").replace("{category}", discoveryMeta.preferredCategory)
                    : discoveryBlock.generic_message || "A fresh rotation from across the house, selected to make every visit feel new."}
                </p>
              </div>
            </div>
            <div className="home-discovery-grid" aria-label="Products selected for you">
              {discoveryProducts.map((product) => (
                <ProductCard product={product} compact key={product.id} />
              ))}
            </div>
            <div className="home-discovery-foot">
              <span>{discoveryBlock.refresh_note || "Selections refresh as you explore"}</span>
              <span className="home-discovery-swipe">{discoveryBlock.swipe_label || "Swipe to discover"} <ChevronRight size={13} /></span>
            </div>
          </div>
        </section>
      )}

      {/* ============ PRIVATE STYLING ============ */}
      {stylingBlock.visible !== false && <section className="private-styling" data-testid="private-styling-section">
        <div className="private-styling-image">
          <img src={stylingImg} alt="Lalita Soujanya styling consultation" loading="lazy" />
        </div>
        <div className="private-styling-copy">
          <span className="eyebrow">{stylingBlock.eyebrow}</span>
          <h2>{stylingBlock.heading}</h2>
          <p>{stylingBlock.body}</p>
          <div className="private-styling-points">
            <span><b>01</b> {stylingBlock.point1}</span>
            <span><b>02</b> {stylingBlock.point2}</span>
            <span><b>03</b> {stylingBlock.point3}</span>
          </div>
          <div className="private-styling-actions">
            <Link to={stylingBlock.primary_cta_link || "/customization"} className="btn btn-primary">{stylingBlock.primary_cta_label || "Explore customisation"} <ArrowUpRight size={15} /></Link>
            <a href={whatsappLink(stylingBlock.whatsapp_message)}
              target="_blank" rel="noreferrer" className="btn btn-outline">
              {stylingBlock.secondary_cta_label} <MessageCircle size={15} />
            </a>
          </div>
        </div>
      </section>}

      {/* ============ OLD DEALS / OFFERS ============ */}
      {dealsBlock.visible !== false && oldDeals.length > 0 && (
        <section className="home-product-section home-product-section--archive" data-testid="old-deals-section">
          <div className="container">
            <div className="section-head">
              <div>
                <span className="eyebrow">{dealsBlock.eyebrow}</span>
                <h2>{dealsBlock.heading}</h2>
                {dealsBlock.description && <p className="sub">{dealsBlock.description}</p>}
              </div>
              <Link to={dealsBlock.cta_link || "/shop?sort=price_asc"} className="text-link" data-testid="old-deals-view-all">
                {dealsBlock.cta_label || "Shop deals"} <ArrowUpRight size={14} />
              </Link>
            </div>
            {dealsBlock.banner_image && (
              <div className="home-archive-banner" data-testid="old-deals-banner">
                <img src={imgUrl(dealsBlock.banner_image)} alt={`${dealsBlock.heading || "Studio deals"} campaign`} loading="lazy" />
              </div>
            )}
            <div className="product-grid">
              {oldDeals.slice(0, 4).map((p) => (
                <ProductCard product={p} key={p.id} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ LAUNCH OFFER ============ */}
      {launchBlock.visible !== false && (
        <section className="launch-offer" data-testid="launch-offer-section">
          <div className="launch-offer-frame">
            <span className="launch-offer-monogram" aria-hidden="true">LS</span>
            <span className="eyebrow">{launchBlock.eyebrow}</span>
            <h2>{launchBlock.heading}</h2>
            <p>
              {launchBlock.body}
            </p>
            <a
              className="btn btn-ghost"
              href={whatsappLink(launchBlock.whatsapp_message || "Hi Lalita Soujanya, I'd like to claim my first-order welcome.")}
              target="_blank"
              rel="noreferrer"
              data-testid="launch-offer-cta"
            >
              {launchBlock.cta_label || "Enquire on WhatsApp"} <ArrowUpRight size={16} />
            </a>
          </div>
        </section>
      )}

      {/* ============ INSTAGRAM ============ */}
      {insta.visible !== false && instaImages.length > 0 && (
        <section className="home-instagram" data-testid="instagram-section">
          <div className="container">
            <div className="section-head">
              <div>
                <span className="eyebrow">{insta.eyebrow}</span>
                <h2>{insta.heading}</h2>
                <p className="sub">{insta.body || "Notes from the studio, new arrivals and the people who wear them."}</p>
              </div>
              <a
                href={`https://instagram.com/${settings.instagram_handle}`}
                target="_blank"
                rel="noreferrer"
                className="text-link"
                data-testid="instagram-link"
              >
                {insta.cta_label || "Follow on Instagram"} <ArrowUpRight size={14} />
              </a>
            </div>
            <div className="instagram-grid">
              {instaImages.slice(0, 8).map((img, i) => (
                <a
                  key={i}
                  href={`https://instagram.com/${settings.instagram_handle}`}
                  target="_blank"
                  rel="noreferrer"
                  className="tile"
                  data-testid={`instagram-tile-${i}`}
                >
                  <img src={imgUrl(img)} alt={`Instagram post ${i + 1}`} loading="lazy" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ NEWSLETTER ============ */}
      {news.visible !== false && (
        <section className="newsletter" data-testid="newsletter-section">
          <div className="newsletter-inner">
            <span className="eyebrow">{news.eyebrow || "The house journal"}</span>
            <h2>{news.heading}</h2>
            <p>{news.body || "Private previews, launch invitations and the occasional note from the studio."}</p>
            <NewsletterForm block={news} />
            <small>{news.note || "Occasional notes from the atelier. Unsubscribe at any time."}</small>
          </div>
        </section>
      )}
    </main>
  );
}
